import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { useSiteTexts } from "../lib/siteTexts";
import { overviewFaqJsonLd, useFaqs } from "../lib/faqs";
import { asList, useKbCategories } from "../lib/kennisbank";
import { PAGE_TEXTS } from "../data/pageTexts";
import { Crumbs } from "../components/product/ProductHero";
import { PhoneLink, QuoteButton, WhatsAppButton } from "../components/home/CtaButtons";
import { FaqItems } from "../components/faq/FaqItems";
import { KbRelated } from "../components/kennisbank/KbRelated";
import { AdviceEndCta } from "../components/info/AdviceBlocks";

const CFG = PAGE_TEXTS.faq;

const AskBox = () => (
  <div className="card p-6" data-testid="faq-ask-box">
    <p className="text-[17px] font-semibold text-ink">Staat uw vraag er niet bij?</p>
    <p className="mt-1.5 text-[14.5px] leading-relaxed text-slate-600">Stel hem gerust. U krijgt een persoonlijk antwoord van ons, meestal dezelfde werkdag.</p>
    <div className="mt-5 flex flex-col items-start gap-4">
      <WhatsAppButton location="faq-page" className="w-full" />
      <PhoneLink location="faq-page" />
      <QuoteButton location="faq-page" label="Prijsindicatie aanvragen" className="w-full !text-[15px]" />
    </div>
  </div>
);

export default function VeelgesteldeVragen() {
  const t = useSiteTexts("faq");
  const seo = t(CFG.blocks.seo);
  const hero = t(CFG.blocks.hero);
  const faqs = useFaqs();
  const cats = useKbCategories();
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const groups = useMemo(() => asList(cats).map((c) => ({ ...c, items: (faqs || []).filter((f) => f.category === c.key && (!needle || `${f.question} ${f.answer}`.toLowerCase().includes(needle))) }))
    .filter((g) => g.items.length), [cats, faqs, needle]);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  useSEO({ title: seo.title, description: seo.body, path: CFG.path, faqJsonLd: faqs?.length ? overviewFaqJsonLd(faqs) : null,
    breadcrumbs: buildBreadcrumbsJsonLd([{ label: "Home", href: "/" }, { label: "Veelgestelde vragen", href: CFG.path }]) });
  return (
    <div data-testid="faq-page">
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="hero-grid absolute inset-y-0 right-0 hidden w-[45%] lg:block" aria-hidden="true" />
        <div className="container-tight relative pb-10 pt-6 sm:pt-8 lg:pb-14">
          <Crumbs items={[{ label: "Home", href: "/" }, { label: "Veelgestelde vragen", href: CFG.path }]} />
          <div className="reveal mt-8 max-w-2xl">
            <p className="eyebrow">Veelgestelde vragen · {faqs?.length || ""} antwoorden</p>
            <h1 className="h1 mt-3 text-balance" data-testid="faq-page-title">{hero.title}</h1>
            <p className="lead mt-4">{hero.body}</p>
            <label className="relative mt-7 block max-w-lg">
              <span className="sr-only">Zoek een vraag</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek een vraag, bijvoorbeeld 'inmeten'"
                className="h-14 w-full rounded-2xl border border-slate-300 bg-white pl-12 pr-4 text-[16px] text-ink shadow-sm placeholder:text-slate-400 focus:border-maas focus:outline-none focus:ring-2 focus:ring-maas/20" data-testid="faq-search" />
            </label>
            {needle && <p className="mt-3 text-[14px] text-slate-500" data-testid="faq-search-count">{total} {total === 1 ? "vraag" : "vragen"} gevonden</p>}
          </div>
        </div>
      </section>
      <section className="section pt-10">
        <div className="container-tight grid gap-10 lg:grid-cols-12 lg:gap-14">
          <aside className="lg:col-span-4">
            <div className="space-y-6 lg:sticky lg:top-28">
              <nav aria-label="Onderwerpen" data-testid="faq-category-nav">
                <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-500">Onderwerpen</p>
                <ul className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-1">
                  {groups.map((g) => (
                    <li key={g.key}><a href={`#${g.key}`} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-[14.5px] text-slate-700 ring-1 ring-slate-200 hover:text-maas lg:ring-0 lg:hover:bg-slate-50" data-testid={`faq-nav-${g.key}`}>
                      {g.label} <span className="text-[12px] text-slate-400">{g.items.length}</span></a></li>
                  ))}
                </ul>
              </nav>
              <div className="hidden lg:block"><AskBox /></div>
            </div>
          </aside>
          <div className="lg:col-span-8">
            {faqs === null ? <div className="h-96 animate-pulse rounded-2xl bg-slate-100" /> : groups.map((g) => (
              <section key={g.key} id={g.key} aria-labelledby={`faq-${g.key}-title`} className="mb-14 scroll-mt-28 last:mb-0" data-testid={`faq-group-${g.key}`}>
                <h2 id={`faq-${g.key}-title`} className="text-[24px] font-bold text-ink sm:text-[28px]">{g.label}</h2>
                {g.description && <p className="mt-1.5 text-[15px] text-slate-600">{g.description}</p>}
                <div className="mt-4"><FaqItems faqs={g.items} testid={`faq-${g.key}-item`} /></div>
              </section>
            ))}
            {faqs && !groups.length && <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-600" data-testid="faq-empty">Geen vragen gevonden. <button type="button" onClick={() => setQ("")} className="font-semibold text-maas hover:underline">Toon alle vragen</button> of stel uw vraag via WhatsApp.</p>}
            <div className="mt-12 lg:hidden"><AskBox /></div>
            <Link to="/blog" className="mt-10 inline-flex items-center gap-1.5 text-[15px] font-semibold text-maas hover:underline" data-testid="faq-kb-link">Uitgebreide uitleg in de kennisbank <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          </div>
        </div>
      </section>
      <KbRelated title="Meer weten? Lees verder in de kennisbank" tone="slate" />
      <AdviceEndCta title="Toe aan nieuwe kozijnen, een schuifpui of een deur?" location="faq-page" />
    </div>
  );
}
